import React, { useMemo } from 'react';
import { IconButton, Typography } from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import {
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    isSameDay,
    isSameMonth,
    isToday,
    isBefore,
    isAfter,
    startOfDay
} from 'date-fns';
import { uk } from 'date-fns/locale';
import { useStyles } from '../styles';

const WEEKDAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'];
const MONTH_NAMES = [
    'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
    'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'
];

const CalendarDay = ({ date, currentMonth, selectedDate, onClick, disabled }) => {
    const classes = useStyles();

    const handleClick = () => {
        if (!disabled) {
            onClick(date);
        }
    };

    const isCurrentMonth = isSameMonth(date, currentMonth);
    const isTodayDate = isToday(date);
    const isSelectedDate = selectedDate && isSameDay(date, selectedDate);

    const dayClasses = [classes.day];
    if (!isCurrentMonth) dayClasses.push(classes.dayOutsideMonth);
    if (isSelectedDate) dayClasses.push(classes.daySelected);
    if (isTodayDate && !isSelectedDate) dayClasses.push(classes.dayToday);

    return (
        <button
            type="button"
            className={dayClasses.join(' ')}
            onClick={handleClick}
            disabled={disabled}
        >
            {date.getDate()}
        </button>
    );
};

const Calendar = ({
    currentMonth,
    selectedDate,
    onPreviousMonth,
    onNextMonth,
    onDateClick,
    disablePast = false,
    disableFuture = false,
    minDate = null,
    maxDate = null
}) => {
    const classes = useStyles();

    const calendarDays = useMemo(() => {
        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(currentMonth);
        const calendarStart = startOfWeek(monthStart, { locale: uk });
        const calendarEnd = endOfWeek(monthEnd, { locale: uk });

        return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
    }, [currentMonth]);

    const monthName = MONTH_NAMES[currentMonth.getMonth()];
    const year = currentMonth.getFullYear();

    const isDateDisabled = (date) => {
        const today = startOfDay(new Date());
        const dateToCheck = startOfDay(date);

        if (disablePast && isBefore(dateToCheck, today)) {
            return true;
        }

        if (disableFuture && isAfter(dateToCheck, today)) {
            return true;
        }

        if (minDate && isBefore(dateToCheck, startOfDay(minDate))) {
            return true;
        }

        if (maxDate && isAfter(dateToCheck, startOfDay(maxDate))) {
            return true;
        }

        return false;
    };

    return (
        <div className={classes.calendarSection}>
            <div className={classes.calendarHeader}>
                <IconButton
                    onClick={onPreviousMonth}
                    className={classes.navigationButton}
                    size="small"
                >
                    <ChevronLeftIcon />
                </IconButton>

                <Typography className={classes.monthYearLabel}>
                    {monthName} {year}
                </Typography>

                <IconButton
                    onClick={onNextMonth}
                    className={classes.navigationButton}
                    size="small"
                >
                    <ChevronRightIcon />
                </IconButton>
            </div>

            <div className={classes.weekdaysRow}>
                {WEEKDAY_LABELS.map((label, index) => (
                    <div key={index} className={classes.weekdayLabel}>
                        {label}
                    </div>
                ))}
            </div>

            <div className={classes.daysGrid}>
                {calendarDays.map((date, index) => (
                    <CalendarDay
                        key={index}
                        date={date}
                        currentMonth={currentMonth}
                        selectedDate={selectedDate}
                        onClick={onDateClick}
                        disabled={isDateDisabled(date)}
                    />
                ))}
            </div>
        </div>
    );
};

export default Calendar;
